import React from "react";
import { Link } from "react-router-dom";
import { Leaf, ShieldX, Check, ArrowRight, Globe, FlaskConical, Quote } from "lucide-react";
import { STORY } from "../mock";
import { useSeo } from "../hooks/useSeo";

const Story = () => {
  useSeo({ title: "Our Story", description: "Why British Supplements makes clean, honest supplements in the UK – no fillers, no stearates and none of the nasties Big Pharma packs into theirs." });
  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#1B4332]">
        <img
          src="https://images.unsplash.com/photo-1500458089283-8c282e703dac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHw0fHxib3RhbmljYWwlMjBncmVlbmVyeXxlbnwwfHx8Z3JlZW58MTc5MDkzMDM4OXww&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0d281d]/70 to-[#1B4332]" />
        <div className="relative mx-auto max-w-4xl px-4 py-20 text-center sm:px-6 lg:py-28">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 font-display text-xs font-semibold uppercase tracking-[0.3em] text-[#95D5B2]">
            <Leaf size={14} /> What We Do
          </span>
          <h1 className="mt-6 font-display text-4xl font-bold uppercase leading-[0.95] text-white sm:text-6xl">
            The Truth About<br />Clean Supplements
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg text-[#cfe0d6]">
            The story Big Pharma doesn't want you to know — and what we're doing differently.
          </p>
        </div>
      </section>

      {/* Founder intro */}
      <section className="mx-auto max-w-4xl px-4 py-16 sm:px-6">
        <div className="rounded-3xl border border-[#e6f0ea] bg-white p-8 sm:p-10">
          <Quote size={34} className="mb-4 text-[#52B788]" />
          <p className="font-display text-xl font-medium leading-relaxed text-[#1B2a21] sm:text-2xl">
            {STORY.intro}
          </p>
          <p className="mt-4 font-display text-sm font-semibold uppercase tracking-wider text-[#2D6A4F]">— Chris, Founder</p>
        </div>
      </section>

      {/* Them vs Us */}
      <section className="mx-auto max-w-5xl px-4 pb-16 sm:px-6">
        <div className="mb-8 text-center">
          <p className="font-display text-xs font-semibold uppercase tracking-[0.3em] text-[#52B788]">The Difference</p>
          <h2 className="mt-2 font-display text-3xl font-bold uppercase text-[#1B2a21] sm:text-4xl">Them vs Us</h2>
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          <div className="rounded-3xl bg-[#fbf3f3] p-8">
            <div className="mb-5 flex items-center gap-2 text-[#c1121f]"><ShieldX size={24} /><span className="font-display text-lg font-bold uppercase">Them</span></div>
            <ul className="space-y-3">
              {STORY.themVsUs.them.map((t, i) => (
                <li key={i} className="flex gap-2 text-[15px] text-[#6b4a4a]"><span className="mt-0.5 font-bold text-[#c1121f]">✗</span>{t}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl bg-[#1B4332] p-8 text-white">
            <div className="mb-5 flex items-center gap-2 text-[#95D5B2]"><Leaf size={24} /><span className="font-display text-lg font-bold uppercase">Us</span></div>
            <ul className="space-y-3">
              {STORY.themVsUs.us.map((t, i) => (
                <li key={i} className="flex gap-2 text-[15px] text-[#dcebe2]"><Check size={18} className="mt-0.5 shrink-0 text-[#74C69D]" />{t}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* What's in your supplements */}
      <section className="bg-[#f0f7f2] py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          <div className="text-center">
            <FlaskConical className="mx-auto mb-3 text-[#2D6A4F]" size={34} />
            <h2 className="font-display text-3xl font-bold uppercase text-[#1B2a21] sm:text-4xl">What's Really In Your Supplements?</h2>
            <p className="mx-auto mt-3 max-w-2xl text-[#4a5c51]">
              Read the back of most bottles and look for the sneaky “other ingredients” tab. Here's the combo of nasty little extras you'll usually find:
            </p>
          </div>
          <div className="mt-8 rounded-3xl border border-dashed border-[#c1121f]/40 bg-white p-6 text-center">
            <p className="text-sm leading-relaxed text-[#8a6a6a]">{STORY.nastyList}</p>
          </div>
          <div className="mt-6 rounded-3xl bg-[#1B4332] p-8 text-center text-white">
            <p className="font-display text-xs font-semibold uppercase tracking-[0.3em] text-[#95D5B2]">So what do we add?</p>
            <p className="mt-2 font-display text-4xl font-bold uppercase text-[#74C69D]">Nothing. Nasty Free.</p>
            <p className="mx-auto mt-3 max-w-xl text-[#cfe0d6]">
              Our products are 100% of what you came to buy. Milk Thistle? Just milk thistle in a veg capsule — plus our uptake blend to help absorption. No fillers to help the machines mass-produce by the millions.
            </p>
          </div>
        </div>
      </section>

      {/* The plan */}
      <section className="mx-auto max-w-4xl px-4 py-16 sm:px-6">
        <div className="mb-8 text-center">
          <Globe className="mx-auto mb-3 text-[#2D6A4F]" size={34} />
          <h2 className="font-display text-3xl font-bold uppercase text-[#1B2a21] sm:text-4xl">What We Want To Do</h2>
          <p className="mx-auto mt-3 max-w-2xl text-[#4a5c51]">With your support, here's the plan — things no one else in the UK is doing.</p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {STORY.plan.map((p, i) => (
            <div key={i} className="flex items-start gap-4 rounded-2xl border border-[#e6f0ea] bg-white p-5">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#2D6A4F] font-display font-bold text-white">{i + 1}</span>
              <p className="text-[15px] leading-relaxed text-[#34463c]">{p}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Values */}
      <section className="mx-auto max-w-4xl px-4 pb-16 sm:px-6">
        <div className="rounded-3xl bg-gradient-to-br from-[#2D6A4F] to-[#1B4332] p-8 text-center text-white sm:p-10">
          <p className="font-display text-xs font-semibold uppercase tracking-[0.3em] text-[#95D5B2]">Built On Old British Values</p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            {STORY.values.map((v) => (
              <span key={v} className="rounded-full bg-white/10 px-5 py-2 font-display text-sm font-semibold uppercase tracking-wider text-white">{v}</span>
            ))}
          </div>
          <Link to="/shop" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#52B788] px-8 py-4 font-display text-sm font-bold uppercase tracking-wider text-[#06160f] transition hover:bg-[#74C69D]">
            Shop Clean Supplements <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Story;
